import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Layout } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Separator } from "../components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft,
  Phone,
  Mail,
  Building2,
  Calendar,
  User,
  Edit,
  UserCheck,
  Plus,
  Clock,
  CheckCircle,
  AlertTriangle
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const VisitorDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, chapter } = useAuth();
  const [visitor, setVisitor] = useState(null);
  const [visitLogs, setVisitLogs] = useState([]);
  const [followups, setFollowups] = useState([]);
  const [members, setMembers] = useState([]);
  const [powerTeams, setPowerTeams] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visitDialogOpen, setVisitDialogOpen] = useState(false);
  const [followupDialogOpen, setFollowupDialogOpen] = useState(false);

  const token = localStorage.getItem("token");
  const primaryColor = chapter?.primary_color || "#CC0000";
  const canEdit = user?.role === "admin" || user?.role === "host";

  const [visitForm, setVisitForm] = useState({ visit_date: "", visit_type: "Chapter" });
  const [followupForm, setFollowupForm] = useState({
    task_type: "Nurture",
    assigned_to_member_id: "",
    due_date: "",
    notes: ""
  });

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [visitorRes, logsRes, fuRes, membersRes, ptRes, catRes] = await Promise.all([
        fetch(`${API}/visitors/${id}`, { headers }),
        fetch(`${API}/visit-logs/${id}`, { headers }),
        fetch(`${API}/followups?visitor_id=${id}`, { headers }),
        fetch(`${API}/members`, { headers }),
        fetch(`${API}/power-teams`, { headers }),
        fetch(`${API}/categories`, { headers })
      ]);

      if (visitorRes.ok) setVisitor(await visitorRes.json());
      if (logsRes.ok) setVisitLogs(await logsRes.json());
      if (fuRes.ok) setFollowups(await fuRes.json());
      if (membersRes.ok) setMembers(await membersRes.json());
      if (ptRes.ok) setPowerTeams(await ptRes.json());
      if (catRes.ok) setCategories(await catRes.json());
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddVisit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API}/visit-logs`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ ...visitForm, visitor_id: id })
      });

      if (response.ok) {
        toast.success("Visit logged");
        setVisitDialogOpen(false);
        setVisitForm({ visit_date: "", visit_type: "Chapter" });
        fetchData();
      }
    } catch (error) {
      toast.error("Error logging visit");
    }
  };

  const handleAddFollowup = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API}/followups`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ ...followupForm, visitor_id: id })
      });

      if (response.ok) {
        toast.success("Follow-up created");
        setFollowupDialogOpen(false);
        setFollowupForm({ task_type: "Nurture", assigned_to_member_id: "", due_date: "", notes: "" });
        fetchData();
      }
    } catch (error) {
      toast.error("Error creating follow-up");
    }
  };

  const handleFollowupStatusChange = async (followupId, status) => {
    try {
      const response = await fetch(`${API}/followups/${followupId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ status })
      });

      if (response.ok) {
        toast.success("Follow-up updated");
        fetchData();
      }
    } catch (error) {
      toast.error("Error updating follow-up");
    }
  };

  const handleConvert = async () => {
    if (!confirm("Convert this visitor to a member?")) return;
    
    try {
      const response = await fetch(`${API}/visitors/${id}/convert`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        toast.success("Converted to member!");
        navigate(`/members/${data.member_id}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Conversion failed");
      }
    } catch (error) {
      toast.error("Error converting visitor");
    }
  };

  const getPowerTeamName = (ptId) => powerTeams.find(pt => pt.power_team_id === ptId)?.name || "-";
  const getCategoryName = (catId) => categories.find(c => c.category_id === catId)?.name || "-";
  const getMemberName = (memberId) => members.find(m => m.member_id === memberId)?.full_name || "-";

  const getInterestLabel = (level) => {
    switch(level) {
      case 1: return { text: "Level 1 - Interested & wants to join", color: "bg-green-100 text-green-700" };
      case 2: return { text: "Level 2 - Needs permission", color: "bg-yellow-100 text-yellow-700" };
      case 3: return { text: "Level 3 - Not the right time", color: "bg-red-100 text-red-700" };
      default: return { text: "Not Set", color: "bg-slate-100 text-slate-700" };
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      Open: "bg-blue-100 text-blue-700",
      "In Progress": "bg-yellow-100 text-yellow-700",
      Done: "bg-green-100 text-green-700",
      Overdue: "bg-red-100 text-red-700"
    };
    return colors[status] || "bg-slate-100 text-slate-700";
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  if (!visitor) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">Visitor not found</p>
          <Button variant="link" onClick={() => navigate("/visitors")}>Go back to visitors</Button>
        </div>
      </Layout>
    );
  }

  const interest = getInterestLabel(visitor.interest_level);

  return (
    <Layout>
      <div className="space-y-6" data-testid="visitor-detail-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/visitors")} data-testid="back-btn">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-display font-bold text-slate-900">{visitor.full_name}</h1>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="outline">{visitor.tag}</Badge>
                <Badge className={`${getStatusBadge(visitor.status)} hover:${getStatusBadge(visitor.status)}`}>
                  {visitor.status}
                </Badge>
              </div>
            </div>
          </div>
          
          <div className="flex gap-2">
            {canEdit && visitor.status === "Active" && user?.role === "admin" && (
              <Button onClick={handleConvert} style={{ backgroundColor: primaryColor }} data-testid="convert-btn">
                <UserCheck className="mr-2 h-4 w-4" />
                Convert to Member
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Contact & Details */}
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-display">Visitor Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-slate-100 rounded-lg">
                      <Building2 className="h-4 w-4 text-slate-600" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Company</p>
                      <p className="font-medium">{visitor.company || "-"}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-slate-100 rounded-lg">
                      <Phone className="h-4 w-4 text-slate-600" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Mobile</p>
                      <p className="font-medium">{visitor.mobile || "-"}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-slate-100 rounded-lg">
                      <Mail className="h-4 w-4 text-slate-600" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Email</p>
                      <p className="font-medium">{visitor.email || "-"}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-slate-100 rounded-lg">
                      <Calendar className="h-4 w-4 text-slate-600" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">First Visit</p>
                      <p className="font-medium">{visitor.first_visit_date || "-"}</p>
                    </div>
                  </div>
                </div>

                <Separator />

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Power Team</p>
                    <p className="font-medium">{getPowerTeamName(visitor.power_team_id)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Category</p>
                    <p className="font-medium">{getCategoryName(visitor.category_id)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Invited By</p>
                    <p className="font-medium">{getMemberName(visitor.invited_by_member_id)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Assigned To</p>
                    <p className="font-medium">{getMemberName(visitor.assigned_to_member_id)}</p>
                  </div>
                </div>

                {visitor.notes && (
                  <>
                    <Separator />
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Notes</p>
                      <p className="text-slate-700">{visitor.notes}</p>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Visit History */}
            <Card className="border-0 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg font-display">Visit History</CardTitle>
                {canEdit && (
                  <Dialog open={visitDialogOpen} onOpenChange={setVisitDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm">
                        <Plus className="mr-2 h-4 w-4" />
                        Log Visit
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Log a Visit</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleAddVisit} className="space-y-4 mt-4">
                        <div className="space-y-2">
                          <Label>Visit Date</Label>
                          <Input
                            type="date"
                            value={visitForm.visit_date}
                            onChange={(e) => setVisitForm({ ...visitForm, visit_date: e.target.value })}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Visit Type</Label>
                          <Select value={visitForm.visit_type} onValueChange={(v) => setVisitForm({ ...visitForm, visit_type: v })}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Chapter">Chapter Meeting</SelectItem>
                              <SelectItem value="1-1">1-on-1</SelectItem>
                              <SelectItem value="Coffee">Coffee Meeting</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex justify-end gap-3 pt-4">
                          <Button type="button" variant="outline" onClick={() => setVisitDialogOpen(false)}>Cancel</Button>
                          <Button type="submit" style={{ backgroundColor: primaryColor }}>Log Visit</Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                )}
              </CardHeader>
              <CardContent>
                {visitLogs.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No visits logged yet</p>
                ) : (
                  <div className="space-y-4">
                    {visitLogs.map((log, index) => (
                      <div key={log.visit_log_id} className="flex items-start gap-4">
                        <div className="flex flex-col items-center">
                          <div 
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: primaryColor }}
                          />
                          {index < visitLogs.length - 1 && (
                            <div className="w-0.5 h-12 bg-slate-200 mt-2" />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">{log.visit_type} Visit</p>
                          <p className="text-sm text-muted-foreground">{log.visit_date}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Follow-ups */}
            <Card className="border-0 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg font-display">Follow-ups</CardTitle>
                {canEdit && (
                  <Dialog open={followupDialogOpen} onOpenChange={setFollowupDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm">
                        <Plus className="mr-2 h-4 w-4" />
                        Add Follow-up
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Create Follow-up Task</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleAddFollowup} className="space-y-4 mt-4">
                        <div className="space-y-2">
                          <Label>Task Type</Label>
                          <Select value={followupForm.task_type} onValueChange={(v) => setFollowupForm({ ...followupForm, task_type: v })}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Close">Close (Interest Level 1)</SelectItem>
                              <SelectItem value="Influence">Influence (Interest Level 2)</SelectItem>
                              <SelectItem value="Nurture">Nurture (Interest Level 3)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Assign To</Label>
                          <Select value={followupForm.assigned_to_member_id} onValueChange={(v) => setFollowupForm({ ...followupForm, assigned_to_member_id: v })}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select member" />
                            </SelectTrigger>
                            <SelectContent>
                              {members.filter(m => m.status === "active").map(m => (
                                <SelectItem key={m.member_id} value={m.member_id}>{m.full_name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Due Date</Label>
                          <Input
                            type="date"
                            value={followupForm.due_date}
                            onChange={(e) => setFollowupForm({ ...followupForm, due_date: e.target.value })}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Notes</Label>
                          <Textarea
                            value={followupForm.notes}
                            onChange={(e) => setFollowupForm({ ...followupForm, notes: e.target.value })}
                            rows={3}
                          />
                        </div>
                        <div className="flex justify-end gap-3 pt-4">
                          <Button type="button" variant="outline" onClick={() => setFollowupDialogOpen(false)}>Cancel</Button>
                          <Button type="submit" style={{ backgroundColor: primaryColor }}>Create</Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                )}
              </CardHeader>
              <CardContent>
                {followups.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No follow-ups created</p>
                ) : (
                  <div className="space-y-4">
                    {followups.map((fu) => (
                      <div key={fu.followup_id} className="p-4 bg-slate-50 rounded-lg">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <Badge className={getStatusBadge(fu.status)}>{fu.status}</Badge>
                              <Badge variant="outline">{fu.task_type}</Badge>
                            </div>
                            <p className="text-sm">
                              <span className="text-muted-foreground">Assigned to:</span>{" "}
                              <span className="font-medium">{getMemberName(fu.assigned_to_member_id)}</span>
                            </p>
                            <p className="text-sm">
                              <span className="text-muted-foreground">Due:</span>{" "}
                              <span className="font-medium">{fu.due_date}</span>
                            </p>
                            {fu.notes && <p className="text-sm mt-2 text-slate-600">{fu.notes}</p>}
                          </div>
                          {canEdit && fu.status !== "Done" && (
                            <Select 
                              value={fu.status} 
                              onValueChange={(v) => handleFollowupStatusChange(fu.followup_id, v)}
                            >
                              <SelectTrigger className="w-[130px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Open">Open</SelectItem>
                                <SelectItem value="In Progress">In Progress</SelectItem>
                                <SelectItem value="Done">Done</SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Interest Level Card */}
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-display">Interest Level</CardTitle>
              </CardHeader>
              <CardContent>
                <Badge className={`${interest.color} text-sm px-3 py-1.5`}>
                  {interest.text}
                </Badge>
                <div className="mt-4 space-y-2 text-sm text-muted-foreground">
                  <p><strong>Level 1:</strong> Interested & wants to join</p>
                  <p><strong>Level 2:</strong> Needs permission from partner</p>
                  <p><strong>Level 3:</strong> Not the perfect time</p>
                </div>
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-display">Quick Stats</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Total Visits</span>
                  <span className="font-bold text-xl">{visitLogs.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Open Follow-ups</span>
                  <span className="font-bold text-xl">
                    {followups.filter(f => f.status === "Open" || f.status === "In Progress").length}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Latest Visit</span>
                  <span className="font-medium">{visitor.latest_visit_date || "-"}</span>
                </div>
              </CardContent>
            </Card>

            {/* Import Metadata */}
            {visitor.import_metadata && (
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg font-display">Import Info</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  {visitor.import_metadata.source_sheet && (
                    <p><span className="text-muted-foreground">Source:</span> {visitor.import_metadata.source_sheet}</p>
                  )}
                  {visitor.import_metadata.open_classification_context && (
                    <p><span className="text-muted-foreground">Context:</span> {visitor.import_metadata.open_classification_context}</p>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default VisitorDetailPage;
